import cloneDeep from "lodash/cloneDeep";
import entries from "lodash/entries";
import sortBy from "lodash/sortBy";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { useImportanceLabelMap } from "PFApp/hooks/use_importance_label_map";
import { getProperty, getVisibleProperties } from "PFCore/helpers/templates";
import { useSkillsType } from "PFCore/hooks";
import { Id, SkillsFramework, Subtemplate, Template } from "PFTypes";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Conflict,
  SkillsFrameworkCustomField,
  SkillsFrameworkCustomValueDecoratedMerged,
  SkillsFrameworkSkillDecoratedMerged
} from "../skills_frameworks_side_panel.types";
import {
  decorateSkillsFrameworks,
  getAllFrameworksCustomFields,
  getDefaultConflictValue,
  getFrameworksString,
  getResolvedAttributes,
  getSkillsGroupedById,
  isInPrimaryFramework,
  stringifySelectedValue
} from "./use_conflict_resolver.utils";

export type ConflictResolution = {
  customFieldId: Id;
  customValueId?: Id;
  newSelected: Conflict["selected"];
};

type ResolvedAttributes = {
  customFields: SkillsFrameworkCustomField<SkillsFrameworkCustomValueDecoratedMerged>[];
  skills: SkillsFrameworkSkillDecoratedMerged[];
};

type UseConflictResolver = {
  selectedFrameworks: SkillsFramework[];
  template: Template;
  subtemplate: Subtemplate | null;
};

export type UseConflictResolverReturn = {
  handleConflictsResolve: (conflictsResolution: ConflictResolution[]) => void;
  conflicts: Conflict[];
  resolvedAttributes: ResolvedAttributes;
};

export const useConflictResolver = ({
  selectedFrameworks,
  template,
  subtemplate
}: UseConflictResolver): UseConflictResolverReturn => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.skillsFrameworks.sidePanel" });
  const skillsType = useSkillsType();
  const experienceLabelMap = useExperienceLabelMap();
  const importanceLabelMap = useImportanceLabelMap();

  const visibleProperties = getVisibleProperties(subtemplate || template);
  const visiblePropertiesNames = visibleProperties
    .filter(({ type }) => type === "custom_field")
    .map(({ name }) => name);

  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [resolvedAttributes, setResolvedAttributes] = useState<ResolvedAttributes>({
    skills: [],
    customFields: []
  });

  const decoratedSkillsFrameworks = useMemo(
    () => decorateSkillsFrameworks(selectedFrameworks),
    [selectedFrameworks]
  );

  const getSkillsConflicts = (): Conflict[] => {
    if (!visiblePropertiesNames.includes("skills")) {
      return [];
    }
    const skillsById = getSkillsGroupedById(decoratedSkillsFrameworks);

    return entries(skillsById)
      .filter(([, skills]) => skills.length > 1)
      .map(([customValueId, skills]) => {
        const sortedSkills = sortBy(
          skills,
          (skill) => (isInPrimaryFramework(skill) ? 1 : 2),
          (value) => value.frameworks.length * -1,
          (value) => value.importance * -1,
          (value) => value.requiredExperience * -1
        ) as SkillsFrameworkSkillDecoratedMerged[];
        const valueSelected = getDefaultConflictValue(sortedSkills) as SkillsFrameworkSkillDecoratedMerged;

        const options = sortedSkills.map(({ skillId, requiredExperience, importance, frameworks }) => ({
          value: stringifySelectedValue({
            customValueId: Number(skillId),
            importance,
            requiredExperience
          }),
          title: `${experienceLabelMap[requiredExperience]}, ${importanceLabelMap[importance]}`,
          subtitle: getFrameworksString(frameworks, t)
        }));

        return {
          conflictTitle: t("skillProficiencyAndImportance", { skill: sortedSkills[0].value }),
          customFieldId: skillsType!.id,
          customValueId: Number(customValueId),
          options,
          selected: {
            customValueId: Number(valueSelected.skillId),
            importance: valueSelected.importance,
            requiredExperience: valueSelected.requiredExperience
          }
        };
      });
  };

  const getCustomFieldsConflicts = (): Conflict[] => {
    const allCustomFields = getAllFrameworksCustomFields(decoratedSkillsFrameworks);

    return allCustomFields
      .filter((customField) => {
        if (!visiblePropertiesNames.includes(customField.type.name)) {
          return false;
        }
        const templateProperty = getProperty(template, customField.type.name);
        const isConflict = templateProperty?.kind === "single" && customField.values.length > 1;

        return isConflict;
      })
      .map((customField) => {
        const sortedValues = sortBy(customField.values, [
          (value) => (isInPrimaryFramework(value) ? 1 : 2),
          (value) => value.frameworks.length * -1,
          (value) => value.value
        ]) as SkillsFrameworkCustomValueDecoratedMerged[];
        const valueSelected = getDefaultConflictValue(
          sortedValues
        ) as SkillsFrameworkCustomValueDecoratedMerged;

        const options = sortedValues.map(({ customValueId, value, frameworks }) => ({
          value: stringifySelectedValue({
            customValueId: Number(customValueId)
          }),
          title: String(value),
          subtitle: getFrameworksString(frameworks, t)
        }));

        const conflict: Conflict = {
          conflictTitle: customField.type.displayAs || "",
          customFieldId: customField.type.id,
          options,
          selected: {
            customValueId: Number(valueSelected.customValueId)
          }
        };

        return conflict;
      });
  };

  const handleConflictsResolve = (conflictsResolution: ConflictResolution[]) => {
    const conflictsCopy = cloneDeep(conflicts);
    conflictsResolution.forEach(({ customFieldId, customValueId, newSelected }) => {
      const conflict = conflictsCopy.find(
        (conflict) => conflict.customFieldId === customFieldId && conflict.customValueId === customValueId
      );

      if (!conflict) {
        return;
      }

      conflict.selected = newSelected;
    });

    setConflicts(conflictsCopy);
    setResolvedAttributes(getResolvedAttributes(decoratedSkillsFrameworks, conflictsCopy, skillsType!.id));
  };

  useEffect(() => {
    const newConflicts = [...getSkillsConflicts(), ...getCustomFieldsConflicts()];
    setConflicts(newConflicts);
    setResolvedAttributes(getResolvedAttributes(decoratedSkillsFrameworks, newConflicts, skillsType!.id));
  }, [decoratedSkillsFrameworks]);

  return { conflicts, resolvedAttributes, handleConflictsResolve };
};
